import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaBox,
  FaCheck,
  FaCheckCircle,
  FaFileInvoice,
  FaFileAlt,
  FaHeart,
  FaInfoCircle,
  FaList,
  FaLock,
  FaMapMarkerAlt,
  FaMinus,
  FaPlus,
  FaShieldAlt,
  FaTimes,
  FaTruck,
  FaUndo,
  FaUserShield,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import { EMPTY_MARKETPLACE_CART } from "../../constants/marketplaceCart";
import {
  addMarketplaceCartItem,
  fetchMarketplaceCart,
  moveMarketplaceCartToWishlist,
  placeMarketplaceCartCheckout,
  removeMarketplaceCartItem,
  saveMarketplaceCartAsList,
  updateMarketplaceCartItem,
} from "../../services/marketplaceCart.service";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { formatCartInr, normalizeMarketplaceCart } from "../../utils/marketplaceCart.util";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/cart_checkout.scss";

const CHECKOUT_STEPS = [
  { id: "review", label: "Cart Review" },
  { id: "address", label: "Delivery Address" },
  { id: "shipping", label: "Shipping Method" },
  { id: "payment", label: "Payment Details" },
];

const SHIPPING_OPTIONS = [
  {
    id: "standard",
    title: "Standard Shipping",
    description: "Economy delivery via road or rail. Best value for bulk B2B orders.",
    price: 0,
    etaDays: 7,
    recommended: true,
  },
  {
    id: "express",
    title: "Express Shipping",
    description: "Priority dispatch with expedited courier handling.",
    price: 499,
    etaDays: 3,
    recommended: false,
  },
  {
    id: "pickup",
    title: "Self Pickup",
    description: "Collect from supplier warehouse. No freight charges apply.",
    price: 0,
    etaDays: 1,
    recommended: false,
  },
];

const WHY_SHOP_ITEMS = [
  "Secure Payment",
  "Trade Protection",
  "GST Invoice",
  "Easy Returns",
  "Verified Suppliers",
];

const GST_RATE = 0.18;

const addDaysLabel = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const defaultListName = () => {
  const date = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `Cart list - ${date}`;
};

function ProductThumb({ name, imageUrl }) {
  if (imageUrl) {
    return (
      <div className="ccheckout-item__thumb">
        <img src={resolvePublicFileUrl(imageUrl)} alt={name} />
      </div>
    );
  }

  return (
    <div className="ccheckout-item__thumb" aria-hidden>
      <FaBox />
    </div>
  );
}

export default function CartCheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cart, setCart] = useState(EMPTY_MARKETPLACE_CART);
  const [activeStep, setActiveStep] = useState("address");
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    label: "Office",
    line: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });
  const [gstNumber, setGstNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [poReference, setPoReference] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [billingAddressMode, setBillingAddressMode] = useState("same_as_delivery");
  const [couponCode, setCouponCode] = useState("");
  const [saveListOpen, setSaveListOpen] = useState(false);
  const [saveListName, setSaveListName] = useState("");
  const [pendingSaveItemIds, setPendingSaveItemIds] = useState(null);

  const sectionRefs = {
    review: useRef(null),
    address: useRef(null),
    shipping: useRef(null),
    payment: useRef(null),
  };

  const applyCart = (data) => {
    setCart(normalizeMarketplaceCart(data) || EMPTY_MARKETPLACE_CART);
  };

  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMarketplaceCart();
      applyCart(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not load cart.");
      setCart(EMPTY_MARKETPLACE_CART);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!StorageService.getToken()) {
      toast.info("Please sign in to checkout");
      navigate("/login", { state: { from: location.pathname } });
      return undefined;
    }
    void loadCart();
    return undefined;
  }, [loadCart, location.pathname, navigate]);

  useEffect(() => {
    let cancelled = false;
    if (!StorageService.getToken()) return undefined;

    CommonService.get("/company/my-profile", {}, { silent: true })
      .then((res) => {
        if (cancelled || !res?.success || !res?.data) return;
        const profile = res.data;
        setCompanyName(profile.company_name || profile.name || "");
        setGstNumber(profile.gstin || profile.gst_number || profile.tax_id || "");

        const addresses = [];
        const addr = profile.address;
        if (addr?.address || addr?.city) {
          addresses.push({
            id: "primary",
            label: "Registered Address",
            isDefault: true,
            line: [addr.address, addr.landmark].filter(Boolean).join(", "),
            city: addr.city || "",
            state: addr.state || "",
            pincode: addr.pincode || "",
            country: addr.country || "India",
          });
        }
        const overseas = profile.overseas_address;
        if (overseas?.address) {
          addresses.push({
            id: "overseas",
            label: "Overseas Address",
            isDefault: false,
            line: overseas.address,
            city: overseas.city || "",
            state: overseas.state || "",
            pincode: overseas.pincode || "",
            country: overseas.country || "",
          });
        }
        setSavedAddresses(addresses);
        if (addresses.length) setSelectedAddressId(addresses[0].id);
      })
      .catch(() => {
        /* profile optional */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const selectedSuppliers = useMemo(
    () =>
      (cart.suppliers || [])
        .map((supplier) => ({
          ...supplier,
          items: (supplier.items || []).filter((item) => item.isSelected),
        }))
        .filter((supplier) => supplier.items.length > 0),
    [cart.suppliers],
  );

  const selectedItems = useMemo(
    () => selectedSuppliers.flatMap((supplier) => supplier.items || []),
    [selectedSuppliers],
  );

  const selectedShipping = SHIPPING_OPTIONS.find((option) => option.id === shippingMethod) || SHIPPING_OPTIONS[0];

  const selectedAddress = useMemo(() => {
    return savedAddresses.find((address) => address.id === selectedAddressId) || savedAddresses[0] || null;
  }, [selectedAddressId, savedAddresses]);

  const totals = useMemo(() => {
    const productTotal = selectedItems.reduce(
      (sum, item) => sum + (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
      0,
    );
    const shipping = selectedShipping.price;
    const subtotal = productTotal;
    const gst = (subtotal + shipping) * GST_RATE;
    const grandTotal = subtotal + shipping + gst;
    const itemUnits = selectedItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return { productTotal, shipping, subtotal, gst, grandTotal, itemUnits, discount: 0 };
  }, [selectedItems, selectedShipping.price]);

  const estimatedDelivery = useMemo(() => {
    const end = addDaysLabel(selectedShipping.etaDays);
    const start = addDaysLabel(Math.max(1, selectedShipping.etaDays - 2));
    return `${start} - ${end}`;
  }, [selectedShipping.etaDays]);

  const scrollToStep = useCallback((stepId) => {
    setActiveStep(stepId);
    sectionRefs[stepId]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const runCartMutation = async (mutation) => {
    setSaving(true);
    try {
      const data = await mutation();
      applyCart(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Cart update failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const updateQuantity = async (item, delta) => {
    const min = item.minOrder || 1;
    const nextQty = Math.max(min, item.quantity + delta);
    if (nextQty === item.quantity) return;
    await runCartMutation(() => updateMarketplaceCartItem(item.id, { quantity: nextQty }));
  };

  const removeItem = async (item) => {
    const data = await runCartMutation(() => removeMarketplaceCartItem(item.id));
    if (data) toast.info("Item removed from cart.");
  };

  const handleMoveToWishlist = async (item) => {
    setSaving(true);
    try {
      const result = await moveMarketplaceCartToWishlist({ itemIds: [item.id], onlySelected: false });
      if (result?.cart) {
        applyCart(result.cart);
        toast.success("Item moved to wishlist.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not move to wishlist.");
    } finally {
      setSaving(false);
    }
  };

  const openSaveListModal = (itemIds = null) => {
    const ids = itemIds || selectedItems.map((item) => item.id);
    if (!ids.length) return;
    setPendingSaveItemIds(ids);
    setSaveListName(defaultListName());
    setSaveListOpen(true);
  };

  const closeSaveListModal = () => {
    setSaveListOpen(false);
    setSaveListName("");
    setPendingSaveItemIds(null);
  };

  const handleSaveAsList = async (event) => {
    event?.preventDefault();
    const name = saveListName.trim();
    if (!name) {
      toast.warning("Enter a list name.");
      return;
    }
    if (!pendingSaveItemIds?.length) {
      closeSaveListModal();
      return;
    }
    setSaving(true);
    try {
      const result = await saveMarketplaceCartAsList(name, { itemIds: pendingSaveItemIds, onlySelected: false });
      closeSaveListModal();
      toast.success(`Saved ${result.savedCount} item(s) to "${result.listName}".`);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Could not save list.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNewAddress = () => {
    if (!newAddress.line.trim() || !newAddress.city.trim()) {
      toast.error("Please enter address and city");
      return;
    }
    const id = `custom-${Date.now()}`;
    const entry = { id, isDefault: false, ...newAddress };
    setSavedAddresses((prev) => [...prev, entry]);
    setSelectedAddressId(id);
    setShowNewAddress(false);
    toast.success("Address saved for this order");
  };

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      toast.warning("Enter a coupon code.");
      return;
    }
    toast.info("Coupon validation is not available yet.");
  };

  const buildOrderNotes = () => {
    const parts = [
      invoiceNotes.trim() ? `Invoice Notes: ${invoiceNotes.trim()}` : null,
      specialInstructions.trim() || null,
      billingAddressMode === "same_as_delivery" ? "Billing address: Same as delivery address" : null,
    ].filter(Boolean);
    return parts.join("\n\n");
  };

  const handlePlaceOrder = async () => {
    if (!selectedItems.length) {
      toast.warning("Select at least one item in your cart to checkout.");
      navigate("/my-cart");
      return;
    }
    if (!selectedAddress?.line?.trim()) {
      toast.error("Please select or add a delivery address");
      scrollToStep("address");
      return;
    }

    setSubmitting(true);
    try {
      const result = await placeMarketplaceCartCheckout({
        itemIds: selectedItems.map((item) => item.id),
        onlySelected: false,
        shippingMethod: selectedShipping.title,
        shippingCharge: selectedShipping.price,
        shippingAddress: {
          label: selectedAddress.label,
          line: selectedAddress.line,
          city: selectedAddress.city,
          state: selectedAddress.state,
          pincode: selectedAddress.pincode,
          country: selectedAddress.country,
        },
        gstNumber: gstNumber.trim() || null,
        companyName: companyName.trim() || null,
        poReference: poReference.trim() || null,
        specialInstructions: buildOrderNotes() || null,
      });

      const orders = result?.orders || [];
      const skipped = result?.skipped || [];
      const paymentAmount = Number(result?.payment_amount) || 0;

      if (skipped.length) {
        toast.warning(`${skipped.length} item(s) could not be ordered and remain in your cart.`);
        await loadCart();
      }

      if (!orders.length) {
        throw new Error("No purchase orders were created.");
      }

      toast.success("Purchase order created — proceed to payment");

      const summaryLines = [
        `Items: ${orders.length}`,
        ...orders.slice(0, 4).map((order) => `${order.product_name} · ${order.po_number}`),
      ];
      if (orders.length > 4) {
        summaryLines.push(`+ ${orders.length - 4} more`);
      }

      navigate("/payment/gateway", {
        state: {
          paymentData: {
            context: "marketplace_checkout",
            summaryTitle:
              orders.length === 1
                ? `Marketplace payment · ${orders[0].po_number}`
                : `Marketplace cart payment · ${orders.length} orders`,
            summaryAmount: formatCartInr(paymentAmount),
            summaryLines,
          },
          marketplacePoPayment: {
            purchaseOrderId: orders[0].purchase_order_id,
            poNumber: orders[0].po_number,
            amount: paymentAmount,
            currency: result?.currency || "INR",
          },
          marketplacePoPayments: orders.map((order) => ({
            purchaseOrderId: order.purchase_order_id,
            poNumber: order.po_number,
            amount: order.payment_amount,
            currency: order.currency || result?.currency || "INR",
            productName: order.product_name,
          })),
          afterSuccessNavigate: "/po-status-tracking",
          afterSuccessState: { poId: orders[0].purchase_order_id },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddFbt = async (product) => {
    const data = await runCartMutation(() => addMarketplaceCartItem(product.id, 1));
    if (data) toast.success(`${product.name} added to cart.`);
  };

  const getStepState = (stepId) => {
    const activeIndex = CHECKOUT_STEPS.findIndex((step) => step.id === activeStep);
    const stepIndex = CHECKOUT_STEPS.findIndex((step) => step.id === stepId);
    if (stepId === "review") return "is-complete";
    if (stepIndex < activeIndex) return "is-complete";
    if (stepIndex === activeIndex) return "is-active";
    return "";
  };

  if (loading) {
    return (
      <div className="ccheckout-page">
        <B2BHubHomeHeader />
        <main className="ccheckout-main"><p className="ccheckout-status">Loading checkout…</p></main>
      </div>
    );
  }

  if (!selectedItems.length) {
    return (
      <div className="ccheckout-page">
        <B2BHubHomeHeader />
        <main className="ccheckout-main">
          <p className="ccheckout-empty">No selected items to checkout.</p>
          <Link to="/my-cart">Back to cart</Link>
        </main>
        <HubHomeFooter />
        <HubFloatingRail />
      </div>
    );
  }

  const itemLabel = selectedItems.length === 1 ? "1 Item" : `${selectedItems.length} Items`;

  return (
    <div className="ccheckout-page">
      <B2BHubHomeHeader />

      <main className="ccheckout-main">
        <nav className="ccheckout-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>›</span>
          <Link to="/my-cart">Cart</Link>
          <span aria-hidden>›</span>
          <span aria-current="page">Checkout</span>
        </nav>

        <header className="ccheckout-hero">
          <div className="ccheckout-hero__title-row">
            <h1>Checkout</h1>
            <span className="ccheckout-hero__secure">
              <FaLock aria-hidden /> Secure Checkout
            </span>
          </div>
          <p>Complete your order in a few simple steps.</p>
        </header>

        <div className="ccheckout-progress" role="navigation" aria-label="Checkout progress">
          {CHECKOUT_STEPS.map((step, idx) => {
            const state = getStepState(step.id);
            const isComplete = state === "is-complete";
            const isReview = step.id === "review";
            return (
              <button
                key={step.id}
                type="button"
                className={`ccheckout-progress__step ${state}`}
                onClick={() => scrollToStep(step.id)}
              >
                <span className="ccheckout-progress__dot">
                  {isComplete ? <FaCheck aria-hidden /> : isReview ? <FaCheck aria-hidden /> : idx + 1}
                </span>
                <span className="ccheckout-progress__label">{step.label}</span>
              </button>
            );
          })}
        </div>

        <div className="ccheckout-layout">
          <div className="ccheckout-left">
            <section className="ccheckout-card" ref={sectionRefs.review} id="checkout-review">
              <div className="ccheckout-card__head">
                <div className="ccheckout-card__title-wrap">
                  <span className="ccheckout-card__num">1</span>
                  <h2 className="ccheckout-card__title">Review Items ({itemLabel})</h2>
                </div>
                <Link to="/my-cart" className="ccheckout-card__action">Edit Cart</Link>
              </div>

              {selectedSuppliers.map((supplier) => (
                <div key={supplier.id}>
                  <div className="ccheckout-supplier">
                    <span className="ccheckout-supplier__label">Seller:</span>
                    <strong>{supplier.name}</strong>
                    {supplier.verified ? (
                      <span className="ccheckout-supplier__verified">
                        <FaCheckCircle aria-hidden /> Verified Supplier
                      </span>
                    ) : null}
                  </div>

                  {(supplier.items || []).map((item) => {
                    const lineTotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0);
                    return (
                      <div key={item.id} className="ccheckout-item">
                        <ProductThumb name={item.name} imageUrl={item.imageUrl} />

                        <div className="ccheckout-item__body">
                          <h3>{item.name}</h3>
                          <div className="ccheckout-item__meta">
                            <span>SKU: {item.sku}</span>
                            <span>MOQ: {item.moq}</span>
                            <span
                              className={`ccheckout-item__stock${
                                item.inStock ? " ccheckout-item__stock--in" : " ccheckout-item__stock--out"
                              }`}
                            >
                              {item.inStock ? "In Stock" : "Out of Stock"}
                            </span>
                            <span>Lead Time: {item.leadTime}</span>
                          </div>
                          <div className="ccheckout-item__links">
                            <button type="button" disabled={saving} onClick={() => handleMoveToWishlist(item)}>
                              <FaHeart aria-hidden /> Save for Later
                            </button>
                            <button type="button" disabled={saving} onClick={() => openSaveListModal([item.id])}>
                              <FaList aria-hidden /> Move to List
                            </button>
                          </div>
                        </div>

                        <div className="ccheckout-item__price">
                          {formatCartInr(item.unitPrice)} / {item.unit}
                        </div>

                        <div className="ccheckout-item__qty">
                          <div className="ccheckout-qty">
                            <button
                              type="button"
                              aria-label="Decrease quantity"
                              disabled={saving}
                              onClick={() => updateQuantity(item, -1)}
                            >
                              <FaMinus aria-hidden />
                            </button>
                            <span>{item.quantity}</span>
                            <button
                              type="button"
                              aria-label="Increase quantity"
                              disabled={saving}
                              onClick={() => updateQuantity(item, 1)}
                            >
                              <FaPlus aria-hidden />
                            </button>
                          </div>
                        </div>

                        <div className="ccheckout-item__total">
                          <strong>{formatCartInr(lineTotal)}</strong>
                          <button
                            type="button"
                            className="ccheckout-item__remove"
                            aria-label={`Remove ${item.name}`}
                            disabled={saving}
                            onClick={() => removeItem(item)}
                          >
                            <FaTimes aria-hidden />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </section>

            <section className="ccheckout-card" ref={sectionRefs.address} id="checkout-address">
              <div className="ccheckout-card__head">
                <div className="ccheckout-card__title-wrap">
                  <span className="ccheckout-card__num">2</span>
                  <h2 className="ccheckout-card__title">Delivery Address</h2>
                </div>
                <button type="button" className="ccheckout-card__action" onClick={() => scrollToStep("address")}>
                  Edit
                </button>
              </div>

              <div className="ccheckout-address-grid">
                {savedAddresses.map((address) => {
                  const isSelected = selectedAddressId === address.id;
                  return (
                    <div
                      key={address.id}
                      className={`ccheckout-address${isSelected ? " is-selected" : ""}`}
                      onClick={() => setSelectedAddressId(address.id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedAddressId(address.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="ccheckout-address__head">
                        <strong><FaMapMarkerAlt aria-hidden /> {address.label}</strong>
                        {isSelected ? (
                          <span className="ccheckout-address__status">
                            <FaCheckCircle aria-hidden /> Selected
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="ccheckout-address__select"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedAddressId(address.id);
                            }}
                          >
                            Select Address
                          </button>
                        )}
                      </div>
                      <p>{address.line}</p>
                      <p>{[address.city, address.state, address.pincode].filter(Boolean).join(", ")}</p>
                      <p>{address.country}</p>
                    </div>
                  );
                })}
              </div>

              <button type="button" className="ccheckout-add-address" onClick={() => setShowNewAddress((value) => !value)}>
                + Add New Address
              </button>

              {showNewAddress ? (
                <div className="ccheckout-new-address ccheckout-form">
                  <div>
                    <label htmlFor="addr-label">Label</label>
                    <input id="addr-label" value={newAddress.label} onChange={(e) => setNewAddress((prev) => ({ ...prev, label: e.target.value }))} />
                  </div>
                  <div className="ccheckout-form__full">
                    <label htmlFor="addr-line">Address</label>
                    <input id="addr-line" value={newAddress.line} onChange={(e) => setNewAddress((prev) => ({ ...prev, line: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="addr-city">City</label>
                    <input id="addr-city" value={newAddress.city} onChange={(e) => setNewAddress((prev) => ({ ...prev, city: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="addr-state">State</label>
                    <input id="addr-state" value={newAddress.state} onChange={(e) => setNewAddress((prev) => ({ ...prev, state: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="addr-pincode">Pincode</label>
                    <input id="addr-pincode" value={newAddress.pincode} onChange={(e) => setNewAddress((prev) => ({ ...prev, pincode: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="addr-country">Country</label>
                    <input id="addr-country" value={newAddress.country} onChange={(e) => setNewAddress((prev) => ({ ...prev, country: e.target.value }))} />
                  </div>
                  <div className="ccheckout-form__full">
                    <button type="button" className="ccheckout-summary__pay" onClick={handleSaveNewAddress}>
                      Save address
                    </button>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="ccheckout-card" ref={sectionRefs.shipping} id="checkout-shipping">
              <div className="ccheckout-card__head">
                <div className="ccheckout-card__title-wrap">
                  <span className="ccheckout-card__num">3</span>
                  <h2 className="ccheckout-card__title">Shipping Method</h2>
                </div>
              </div>

              <div className="ccheckout-shipping">
                {SHIPPING_OPTIONS.map((option) => (
                  <label
                    key={option.id}
                    className={`ccheckout-shipping-option${shippingMethod === option.id ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="shipping"
                      checked={shippingMethod === option.id}
                      onChange={() => setShippingMethod(option.id)}
                    />
                    <div>
                      <p className="ccheckout-shipping-option__title">
                        <FaTruck aria-hidden /> {option.title}
                        {option.recommended ? <span className="ccheckout-shipping-option__badge">Recommended</span> : null}
                      </p>
                      <p className="ccheckout-shipping-option__meta">
                        {option.description}
                        <br />
                        Est. Delivery: <strong>{addDaysLabel(option.etaDays)}</strong>
                      </p>
                    </div>
                    <span className="ccheckout-shipping-option__price">
                      {option.price === 0 ? "FREE" : formatCartInr(option.price)}
                    </span>
                  </label>
                ))}
              </div>

              <p className="ccheckout-shipping-note">
                Need faster delivery? Contact our support for custom shipping solutions.
              </p>
            </section>

            <section className="ccheckout-card" ref={sectionRefs.payment} id="checkout-payment">
              <div className="ccheckout-card__head">
                <div className="ccheckout-card__title-wrap">
                  <span className="ccheckout-card__num">4</span>
                  <h2 className="ccheckout-card__title">GST &amp; Business Information</h2>
                </div>
              </div>

              <div className="ccheckout-form">
                <div>
                  <label htmlFor="gst">GST Number*</label>
                  <input id="gst" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="22AAAAA0000A1Z5" />
                </div>
                <div>
                  <label htmlFor="billing-company">Company Name*</label>
                  <input id="billing-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </div>
                <div>
                  <label htmlFor="po-ref">Purchase Order Reference</label>
                  <input id="po-ref" value={poReference} onChange={(e) => setPoReference(e.target.value)} placeholder="Optional internal PO number" />
                </div>
                <div>
                  <label htmlFor="billing-address">Billing Address</label>
                  <select
                    id="billing-address"
                    value={billingAddressMode}
                    onChange={(e) => setBillingAddressMode(e.target.value)}
                  >
                    <option value="same_as_delivery">Same as Delivery Address</option>
                    <option value="registered">Use Registered Address</option>
                  </select>
                </div>
                <div className="ccheckout-form__full">
                  <label htmlFor="invoice-notes">Invoice Notes</label>
                  <textarea
                    id="invoice-notes"
                    rows={3}
                    value={invoiceNotes}
                    onChange={(e) => setInvoiceNotes(e.target.value)}
                    placeholder="Notes to appear on invoice…"
                  />
                </div>
                <div className="ccheckout-form__full">
                  <label htmlFor="instructions">Special Instructions</label>
                  <textarea
                    id="instructions"
                    rows={3}
                    value={specialInstructions}
                    onChange={(e) => setSpecialInstructions(e.target.value)}
                    placeholder="Packaging, delivery window, invoicing notes…"
                  />
                </div>
              </div>

              <div className="ccheckout-trust-row">
                <div className="ccheckout-trust-pill"><span><FaShieldAlt aria-hidden /></span>Secure Payments</div>
                <div className="ccheckout-trust-pill"><span><FaFileInvoice aria-hidden /></span>GST Invoice</div>
                <div className="ccheckout-trust-pill"><span><FaUserShield aria-hidden /></span>Trade Assurance</div>
                <div className="ccheckout-trust-pill"><span><FaUndo aria-hidden /></span>Easy Returns</div>
              </div>
            </section>
          </div>

          <aside className="ccheckout-summary">
            <div className="ccheckout-summary__card">
              <h2 className="ccheckout-summary__title">Order Summary</h2>

              <div className="ccheckout-summary__lines">
                <div>
                  <span>Items ({totals.itemUnits})</span>
                  <strong>{formatCartInr(totals.productTotal)}</strong>
                </div>
                <div>
                  <span>Shipping Charges</span>
                  <strong>{selectedShipping.price === 0 ? "FREE" : formatCartInr(totals.shipping)}</strong>
                </div>
                <div>
                  <span>GST (18%) <FaInfoCircle aria-hidden /></span>
                  <strong>{formatCartInr(totals.gst)}</strong>
                </div>
              </div>

              <div className="ccheckout-summary__coupon">
                <div className="ccheckout-summary__coupon-head">
                  <span>Have a coupon?</span>
                  <button type="button" onClick={handleApplyCoupon}>Apply</button>
                </div>
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  placeholder="Enter coupon code"
                />
              </div>

              <div className="ccheckout-summary__breakdown">
                <div><span>Subtotal</span><span>{formatCartInr(totals.subtotal)}</span></div>
                <div><span>GST (18%)</span><span>{formatCartInr(totals.gst)}</span></div>
                <div><span>Shipping</span><span>{selectedShipping.price === 0 ? "FREE" : formatCartInr(totals.shipping)}</span></div>
              </div>

              <div className="ccheckout-summary__grand">
                <span>Grand Total</span>
                <strong>{formatCartInr(totals.grandTotal)}</strong>
              </div>

              <p className="ccheckout-summary__save">You save {formatCartInr(totals.discount)} on this order</p>

              <div className="ccheckout-summary__delivery">
                <FaTruck aria-hidden />
                <div>
                  <span>Estimated Delivery</span>
                  <strong>{estimatedDelivery}</strong>
                </div>
              </div>

              <button
                type="button"
                className="ccheckout-summary__pay"
                onClick={handlePlaceOrder}
                disabled={submitting || saving}
              >
                {submitting ? "Creating purchase orders…" : "Proceed to Payment"}
                <FaArrowRight aria-hidden />
              </button>

              <button
                type="button"
                className="ccheckout-summary__draft"
                disabled={submitting || saving}
                onClick={() => openSaveListModal()}
              >
                <FaFileAlt aria-hidden /> Save as Draft
              </button>

              <div className="ccheckout-summary__why">
                <h3>Why shop with Trocanet?</h3>
                <ul>
                  {WHY_SHOP_ITEMS.map((item) => (
                    <li key={item}><FaCheckCircle aria-hidden /> {item}</li>
                  ))}
                </ul>
              </div>

              <div className="ccheckout-summary__payments">
                <span>We accept</span>
                <div className="ccheckout-summary__payment-icons" aria-hidden>
                  <span>VISA</span>
                  <span>MC</span>
                  <span>UPI</span>
                  <span>RuPay</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {cart.frequentlyBoughtTogether?.length ? (
          <section className="ccheckout-fbt" aria-label="You may also need">
            <div className="ccheckout-fbt__head">
              <h2>You may also need</h2>
              <Link to="/marketplace/products">View More</Link>
            </div>
            <div className="ccheckout-fbt__grid">
              {cart.frequentlyBoughtTogether.map((product) => (
                <article key={product.id} className="ccheckout-fbt__card">
                  <div className="ccheckout-fbt__thumb">
                    {product.imageUrl ? (
                      <img src={resolvePublicFileUrl(product.imageUrl)} alt={product.name} />
                    ) : (
                      <FaBox />
                    )}
                  </div>
                  <h3>{product.name}</h3>
                  <p>
                    {formatCartInr(product.price)} / {product.unit}
                  </p>
                  <button
                    type="button"
                    className="ccheckout-fbt__add"
                    disabled={saving}
                    onClick={() => handleAddFbt(product)}
                  >
                    Add
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </main>

      <HubHomeFooter />
      <HubFloatingRail />

      {saveListOpen ? (
        <div className="ccheckout-modal" role="dialog" aria-modal="true" aria-labelledby="ccheckout-save-list-title">
          <button
            type="button"
            className="ccheckout-modal__backdrop"
            aria-label="Close save list dialog"
            onClick={closeSaveListModal}
          />
          <div className="ccheckout-modal__panel">
            <div className="ccheckout-modal__head">
              <h2 id="ccheckout-save-list-title">Save as List</h2>
              <button type="button" className="ccheckout-modal__close" onClick={closeSaveListModal} aria-label="Close">
                <FaTimes aria-hidden />
              </button>
            </div>
            <form className="ccheckout-modal__body" onSubmit={handleSaveAsList}>
              <label htmlFor="ccheckout-save-list-name">List name</label>
              <input
                id="ccheckout-save-list-name"
                type="text"
                value={saveListName}
                onChange={(event) => setSaveListName(event.target.value)}
                placeholder="Enter list name"
                maxLength={255}
                autoFocus
                disabled={saving}
              />
              <p className="ccheckout-modal__hint">
                {pendingSaveItemIds?.length || 0} item(s) will be saved to this list.
              </p>
              <div className="ccheckout-modal__actions">
                <button type="button" className="ccheckout-modal__cancel" onClick={closeSaveListModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ccheckout-modal__submit" disabled={saving}>
                  Save List
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
